import {
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import MUITextField from "@material-ui/core/TextField";
import Icon from "@material-ui/icons/BugReport";
import { format } from "date-fns";
import { capitalize } from "lodash";
import React, { FC, Fragment } from "react";
import {
  AutocompleteInput,
  Create,
  CreateProps,
  Datagrid,
  DateField,
  DateInput,
  Edit,
  EditProps,
  Filter,
  FormDataConsumer,
  FunctionField,
  List,
  ListProps,
  Record,
  ReferenceField,
  ReferenceInput,
  SaveButton,
  SelectInput,
  Show,
  ShowProps,
  SimpleForm,
  SimpleShowLayout,
  TextField,
  TextInput,
  Toolbar,
  useDataProvider,
  useListContext,
  useNotify,
  useRefresh,
  useUnselectAll,
} from "react-admin";
import CrudBreadcrumb from "../components/CrudBreadcrumb";
import { BookingIdField } from "../components/fields";
import ProductInstanceField from "../components/fields/ProductInstanceIdField";
import StatusField from "../components/StatusField";
import TitleBreadcrumb from "../components/TitleBreadcrumb";
import { foundIn } from "../utils";

const resourceName = "issues";

interface Props {
  selectedIds?: number[];
  onComplete?: () => void;
}

const ResolveMultipleIssueDialog: React.FC<Props> = ({
  selectedIds,
  onComplete,
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [open, setOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [closingRemarks, setClosingRemarks] = React.useState("");
  const [resolvableIssues, setResolvableIssues] = React.useState<any[]>([]);

  const hasSelection = Boolean(selectedIds && selectedIds.length > 0);

  const handleOpenDialog = () => {
    if (!hasSelection) {
      notify("Please select issues first", "warning");
      return;
    }

    setResolvableIssues(selectedIds!.map((id) => ({ id })));
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setClosingRemarks("");
  };

  const handleAccept = async () => {
    setIsLoading(true);

    try {
      const updatePromises = resolvableIssues.map((issue) =>
        dataProvider.update("issues", {
          id: issue.id,
          data: {
            status: IssueStatus.RESOLVED,
            closing_remarks: closingRemarks,
          },
          previousData: issue,
        }),
      );

      await Promise.all(updatePromises);

      notify(
        `Successfully resolved ${resolvableIssues.length} issues.`,
        "info",
      );

      setOpen(false);
      setClosingRemarks("");
      onComplete?.();
    } catch (error: any) {
      notify(`Error: ${error.message}`, "warning");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button
        color="primary"
        onClick={handleOpenDialog}
        size="small"
        disabled={!hasSelection}
      >
        Resolve Issues {hasSelection ? `(${selectedIds!.length})` : ""}
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="resolve-multiple-issues-title"
      >
        <DialogTitle id="resolve-multiple-issues-title">
          Resolve Issues
        </DialogTitle>

        <DialogContent>
          <DialogContentText>
            You have <strong>{selectedIds?.length || 0} issues</strong>{" "}
            selected.
            <br />
            Please confirm resolving these issues.
          </DialogContentText>

          <MUITextField
            fullWidth
            multiline
            rows={3}
            label="Closing Remarks (optional)"
            value={closingRemarks}
            onChange={(e) => setClosingRemarks(e.target.value)}
            margin="dense"
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleAccept} color="primary" disabled={isLoading}>
            {isLoading
              ? "Resolving..."
              : `Resolve ${selectedIds?.length || 0} Issues`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

interface IssueBulkActionsProps {
  selectedIds?: number[];
  data?: any[];
}

export const IssueBulkActions: React.FC<IssueBulkActionsProps> = ({
  selectedIds,
}) => {
  const refresh = useRefresh();
  const unselectAll = useUnselectAll("issues");
  const listContext = useListContext();

  const statusFilter = listContext?.filterValues?.status;
  const hasSelection = Boolean(selectedIds && selectedIds.length > 0);

  const handleActionComplete = () => {
    refresh();
    unselectAll();
  };

  const canBulkResolve =
    hasSelection && (!statusFilter || statusFilter === IssueStatus.OPEN);

  return (
    <Fragment>
      {canBulkResolve && (
        <ResolveMultipleIssueDialog
          selectedIds={selectedIds || []}
          onComplete={handleActionComplete}
        />
      )}
    </Fragment>
  );
};

const ResourceList: FC<ListProps> = (props) => {
  const isRowSelectable = (record: Record): boolean => {
    // Allow selection for deliveries that can be assigned, started, finished, or verified
    return foundIn(record.status, [IssueStatus.OPEN]);
  };

  return (
    <List
      {...props}
      exporter={false}
      filters={<ListFilter />}
      bulkActionButtons={<IssueBulkActions />}
    >
      <Datagrid rowClick="show" isRowSelectable={isRowSelectable}>
        <TextField source="id" />
        <DateField source="created_at" showTime={true} />
        <BookingIdField source="booking" />
        <ProductInstanceField source="product_instance" />

        <ReferenceField
          source="reporter_id"
          reference="consultants"
          label="Reported By"
        >
          <TextField source="full_name" />
        </ReferenceField>
        <TextField source="description" />
        <DateField source="resolved_at" label="Resolved At" showTime={true} />
        <FunctionField
          label="Resolved By"
          render={(record: any) =>
            record?.resolved_by?.admin?.full_name ??
            record?.resolved_by?.driver?.full_name ??
            record?.resolved_by?.consultant?.full_name ??
            "-"
          }
        />
        <StatusField source="status" />
      </Datagrid>
    </List>
  );
};

const ListFilter: FC<any> = (props) => (
  <Filter {...props}>
    <TextInput source="id" label="ID" />
    <DateInput source="date" />
    <SelectInput
      source="status"
      allowEmpty
      emptyText="All"
      choices={[
        { id: IssueStatus.OPEN, name: "Open" },
        { id: IssueStatus.RESOLVED, name: "Resolved" },
      ]}
    />
    <ReferenceInput
      label="Set"
      source="product_instance_id"
      reference="product-instances"
      allowEmpty
    >
      <AutocompleteInput optionText="name" />
    </ReferenceInput>
    <ReferenceInput
      label="Report by"
      source="reporter_id"
      reference="consultants"
      allowEmpty
    >
      <AutocompleteInput optionText="full_name" />
    </ReferenceInput>
  </Filter>
);

const ResourceShow: FC<ShowProps> = (props) => {
  const breadcrumbPaths = [
    { link: `/${resourceName}`, title: `${capitalize(resourceName)}` },
    { link: `/${resourceName}/${props.id}/show`, title: `${props.id}` },
  ];

  return (
    <>
      <TitleBreadcrumb paths={breadcrumbPaths} />
      <Show {...props}>
        <SimpleShowLayout>
          <TextField source="id" />
          <DateField source="updated_at" showTime={true} />
          <BookingIdField source="booking" />
          <ProductInstanceField source="product_instance" />
          <ReferenceField
            source="reporter_id"
            reference="consultants"
            label="Reported By"
          >
            <TextField source="full_name" />
          </ReferenceField>
          <TextField source="status" />
          <TextField source="description" />
          <TextField source="warehouse_note" />
          <TextField source="closing_remarks" />
          <DateField source="resolved_at" label="Resolved At" showTime={true} />
          <FunctionField
            label="Resolved By"
            render={(record: any) =>
              record?.resolved_by?.admin?.full_name ??
              record?.resolved_by?.driver?.full_name ??
              record?.resolved_by?.consultant?.full_name ??
              "-"
            }
          />
        </SimpleShowLayout>
      </Show>
    </>
  );
};

const IssueEditToolbar: FC<any> = (props) => (
  <Toolbar {...props}>
    <SaveButton disabled={props.pristine} />
  </Toolbar>
);

const ResourceEdit: FC<EditProps> = (props) => (
  <Edit {...props}>
    <SimpleForm toolbar={<IssueEditToolbar />}>
      <TextField source="id" />
      <DateField source="updated_at" showTime={true} />
      <ReferenceInput source="booking_id" reference="bookings">
        <AutocompleteInput
          optionText={(record) => {
            if (!record) return "";

            const createdAt = record.date
              ? format(new Date(record.date), "dd/MM/yyyy HH:mm")
              : "-";

            const surgeonName = record.surgeon?.full_name ?? "-";

            return `${record.id ?? "-"} - ${createdAt} - ${surgeonName}`;
          }}
        />
      </ReferenceInput>
      <ReferenceInput
        label="Product Instance"
        source="product_instance_id"
        reference="product-instances"
        sort={{ field: "name", order: "asc" }}
        perPage={1000}
      >
        <AutocompleteInput optionText="name" />
      </ReferenceInput>
      <ReferenceInput source="reporter_id" reference="consultants">
        <SelectInput optionText="full_name" />
      </ReferenceInput>
      <SelectInput
        source="status"
        choices={[
          { id: IssueStatus.OPEN, name: "Open" },
          { id: IssueStatus.RESOLVED, name: "Resolved" },
        ]}
      />
      <TextInput source="description" />
      <TextInput source="warehouse_note" />
      <FormDataConsumer>
        {({ formData, ...rest }) =>
          formData.status === IssueStatus.RESOLVED && (
            <TextInput source="closing_remarks" />
          )
        }
      </FormDataConsumer>
    </SimpleForm>
  </Edit>
);

const ResourceCreate: FC<CreateProps> = (props) => {
  return (
    <>
      <CrudBreadcrumb
        resourceName={Common.resourceName}
        resourceId={""}
        mode="create"
      />
      <Create {...props}>
        <SimpleForm>
          <ReferenceInput source="booking_id" reference="bookings">
            <AutocompleteInput
              optionText={(record) => {
                if (!record) return "";

                const createdAt = record.date
                  ? format(new Date(record.date), "dd/MM/yyyy HH:mm")
                  : "-";

                const surgeonName = record.surgeon?.full_name ?? "-";

                return `${record.id ?? "-"} - ${createdAt} - ${surgeonName}`;
              }}
            />
          </ReferenceInput>
          <ReferenceInput
            label="Product Instance"
            source="product_instance_id"
            reference="product-instances"
            sort={{ field: "name", order: "ASC" }}
            perPage={1000}
          >
            <AutocompleteInput optionText="name" />
          </ReferenceInput>
          <ReferenceInput source="reporter_id" reference="consultants">
            <SelectInput optionText="full_name" />
          </ReferenceInput>
          <TextInput source="description" />
          <TextInput source="warehouse_note" />
        </SimpleForm>
      </Create>
    </>
  );
};

const Common = {
  resourceName: "issues",
};

// eslint-disable-next-line import/no-anonymous-default-export
export default {
  name: resourceName,
  icon: Icon,
  list: ResourceList,
  show: ResourceShow,
  edit: ResourceEdit,
  create: ResourceCreate,
};

export const IssueStatus = {
  OPEN: "open",
  RESOLVED: "resolved",
};
